// Cards stay with the answer that made them. A card that still needs the
// person moves under the latest answer only when that answer is about it
// (names its study or plan); otherwise the latest answer carries a small
// "still waiting" link to it. Once decided, a card that moved stays under the
// answer that was latest when they decided, so the receipt is where they
// acted, also after a reload. A moved card's first place keeps a pointer.
import { useEffect, useRef } from "react";
import { toDisplayBrand } from "../../utils/displayBrand";
import { cleanTitle } from "./TaskCard";

const WAITS = new Set(["proposed", "waiting_approval"]);
const plain = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
/** Whether an answer is about a card: it names the card's study or plan. */
const mentions = (answer, names) => {
  const text = ` ${plain(answer && answer.content)} `;
  return names.map(plain).filter((n) => n.length >= 6).some((n) => text.includes(` ${n} `) || text.includes(` ${n.split(" ").slice(0, 4).join(" ")} `));
};
const planNames = (p) => [p && p.summary, ...((p && p.actions) || []).map((a) => a.params && (a.params.study_name || a.params.name))].filter(Boolean);
const taskNames = (t) => [t && t.study_config && t.study_config.name, t && t.title, t && t.study_name].filter(Boolean).map((x) => cleanTitle(x));
const at = (d) => (d ? new Date(d).getTime() : NaN);

/**
 * Returns placeOf(message) -> { plan_ids, task_ids, pointers, moved, waiting },
 * where moved ({ plan_ids, groups }) holds the cards brought under this answer
 * and waiting ({ count, at }) points the latest answer at cards still waiting
 * under an earlier one (at: that answer's id).
 */
export default function useCardPlaces(chatId, shown, plans, tasks) {
  // Older cards carry no decision time: one acted on in this page stays put.
  const followed = useRef(new Set());
  useEffect(() => { followed.current = new Set(); }, [chatId]);

  const answers = shown.filter((m) => m.role === "assistant" && m._id);
  const last = answers[answers.length - 1];
  // The answer that was latest at a given time.
  const answerAt = (t) => {
    let hit = null;
    for (const m of answers) if (at(m.createdAt) <= t) hit = m;
    return hit;
  };
  const out = {};
  for (const m of answers) out[m._id] = { plan_ids: [], task_ids: [], pointers: [], moved: null, waiting: null };
  // Cards still waiting under an earlier answer, for the latest one's link.
  const waitingAt = [];
  const bring = (target, kind, item) => {
    const slot = out[target._id];
    slot.moved = slot.moved || { plan_ids: [], groups: [] };
    slot.moved[kind].push(item);
  };
  const order = (m) => answers.indexOf(m);

  for (const m of answers) {
    const slot = out[m._id];
    for (const pid of m.plan_ids || []) {
      const p = plans[pid];
      let target = m;
      if (p && p.status === "proposed" && !(p.blocking || []).length) target = followed.current.has(pid) || mentions(last, planNames(p)) ? last : m;
      else if (p && p.decided_at) target = answerAt(at(p.decided_at)) || m;
      else if (p && followed.current.has(pid)) target = last;
      if (order(target) > order(m)) {
        if (p && p.status === "proposed") followed.current.add(pid);
        bring(target, "plan_ids", pid);
        slot.pointers.push(`Moved to a later answer: ${toDisplayBrand(p.summary || "plan")}`);
      } else {
        slot.plan_ids.push(pid);
        if (p && p.status === "proposed" && !(p.blocking || []).length && m !== last) waitingAt.push(m._id);
      }
    }
    const tids = (m.task_ids || []).filter((t) => tasks[t]);
    let target = m;
    const waits = tids.some((t) => WAITS.has(tasks[t].status));
    if (waits) target = tids.some((t) => followed.current.has(t)) || mentions(last, tids.flatMap((t) => taskNames(tasks[t]))) ? last : m;
    else {
      const decided = Math.max(...tids.map((t) => at(tasks[t].decided_at)).filter((x) => !Number.isNaN(x)), -Infinity);
      if (decided > -Infinity) target = answerAt(decided) || m;
      else if (tids.some((t) => followed.current.has(t))) target = last;
    }
    if (tids.length && order(target) > order(m)) {
      if (tids.some((t) => WAITS.has(tasks[t].status))) tids.forEach((t) => followed.current.add(t));
      bring(target, "groups", tids);
      slot.pointers.push(tids.length === 1
        ? `Moved to a later answer: ${toDisplayBrand(cleanTitle(tasks[tids[0]].study_config?.name || tasks[tids[0]].title || "card"))}`
        : `Moved to a later answer: ${tids.length} cards`);
    } else {
      slot.task_ids = m.task_ids || [];
      if (waits && m !== last) tids.filter((t) => WAITS.has(tasks[t].status)).forEach(() => waitingAt.push(m._id));
    }
  }
  if (last && waitingAt.length) out[last._id].waiting = { count: waitingAt.length, at: waitingAt[0] };
  return (m) => out[m._id] || { plan_ids: m.plan_ids || [], task_ids: m.task_ids || [], pointers: [], moved: null, waiting: null };
}
